export type N8nRefLetterPayload = {
  applicationId: string;
  platformCallbackUrl?: string;
  selectClient: string;
  classification: string;
  letterNumber: string;
  caseworkerEmail: string;
  refereeId: string;
  refereeFullName: string;
  refereeFirstName: string;
  refereeLastName: string;
  refereeEmail: string;
  refereePhone: string;
  refereeBackground: string;
  refereeTypeSlug: string;
  relationshipCapacity: string;
  howLongKnown: string;
  refereeRawInput: string;
  refereeCompanyNow: string;
  refereeCompanyTogether: string;
  refereeWorkedTogether: string;
  refereeLinkedIn: string;
  refereeHowLongKnowYou: string;
  refereeAssessment: string;
  refereeMeetDesc: string;
  refereePersonDesc: string;
  refereeConclusion: string;
  refereeMeetActivities: string;
  refereeJointProject: string;
  refereeJointExternal: string;
  refereePossibleWorks: string;
  refereeScoringResult: number | null;
  targetCriteria: string;
  englishVariant: string;
  tonePreference: string;
  additionalContext: string;
};

const N8N_LETTER_NUMBER_MAP: Record<string, string> = {
  letter_1_referee_first: "1",
  letter_2_project_first: "2",
  letter_3_applicant_first: "3",
};

const N8N_TARGET_CRITERIA_MAP: Record<string, string> = {
  mc_oc1: "mc,oc1",
  mc_oc2: "mc,oc2",
  mc_oc3: "mc,oc3",
  mc_oc1_oc2: "mc,oc1,oc2",
  mc_oc1_oc3: "mc,oc1,oc3",
  mc_oc2_oc3: "mc,oc2,oc3",
  mc_oc1_oc2_oc3: "mc,oc1,oc2,oc3",
  all_criteria: "mc,oc1,oc2,oc3",
};

const N8N_ENGLISH_VARIANT_MAP: Record<string, string> = {
  gb_british: "British",
  us_american: "American",
};

const N8N_TONE_MAP: Record<string, string> = {
  warm_professional: "Warm professional",
  formal: "Formal",
  technical: "Technical",
};

export const DEFAULT_REFEREE_TYPE_SLUG = "engineering_manager_cto";
export const DEFAULT_CLASSIFICATION = "Exceptional Talent";
export const DEFAULT_LETTER_NUMBER = "1";
export const DEFAULT_TARGET_CRITERIA = "mc,oc1";
export const DEFAULT_ENGLISH_VARIANT = "British";
export const DEFAULT_TONE_PREFERENCE = "Warm professional";

export function mapLetterNumberToN8n(value?: string | null): string {
  if (!value) return DEFAULT_LETTER_NUMBER;
  return N8N_LETTER_NUMBER_MAP[value] ?? value;
}

export function mapTargetCriteriaToN8n(value?: string | null): string {
  if (!value) return DEFAULT_TARGET_CRITERIA;
  if (N8N_TARGET_CRITERIA_MAP[value]) {
    return N8N_TARGET_CRITERIA_MAP[value];
  }
  // Flexible chip-serialized values: oc1_oc2 → oc1,oc2
  if (/^[a-z0-9]+(_[a-z0-9]+)*$/.test(value)) {
    return value.split("_").join(",");
  }
  return value;
}

export function mapEnglishVariantToN8n(value?: string | null): string {
  if (!value) return DEFAULT_ENGLISH_VARIANT;
  return N8N_ENGLISH_VARIANT_MAP[value] ?? value;
}

export function mapTonePreferenceToN8n(value?: string | null): string {
  if (!value) return DEFAULT_TONE_PREFERENCE;
  return N8N_TONE_MAP[value] ?? value;
}

export function getPhoneDigitsOnly(phone: string): string {
  return phone.replace(/\D/g, "");
}

export class N8nRefLetterValidationError extends Error {
  missingFields: string[];

  constructor(missingFields: string[]) {
    super(
      `Missing required n8n ref letter fields: ${missingFields.join(", ")}`
    );
    this.name = "N8nRefLetterValidationError";
    this.missingFields = missingFields;
  }
}

export function validateN8nRefLetterPayload(
  payload: N8nRefLetterPayload
): void {
  const missingFields: string[] = [];

  if (!payload.selectClient?.trim()) {
    missingFields.push("selectClient");
  }

  if (!payload.refereeFullName?.trim() && !payload.refereeFirstName?.trim()) {
    missingFields.push("refereeFullName");
  }

  if (!payload.refereeEmail?.includes("@")) {
    missingFields.push("refereeEmail");
  }

  if (getPhoneDigitsOnly(payload.refereePhone ?? "").length < 7) {
    missingFields.push("refereePhone");
  }

  if (!payload.refereeBackground?.trim()) {
    missingFields.push("refereeBackground");
  }

  if (!payload.refereeTypeSlug?.trim()) {
    missingFields.push("refereeTypeSlug");
  }

  if (!payload.relationshipCapacity?.trim()) {
    missingFields.push("relationshipCapacity");
  }

  if (!payload.howLongKnown?.trim()) {
    missingFields.push("howLongKnown");
  }

  const caseworkerEmail = payload.caseworkerEmail?.trim() ?? "";
  if (
    !caseworkerEmail.includes("@") ||
    !/@immigram\.io$/i.test(caseworkerEmail)
  ) {
    missingFields.push("caseworkerEmail");
  }

  if (missingFields.length > 0) {
    throw new N8nRefLetterValidationError(missingFields);
  }
}
